import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  BellOff,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Mail,
  Star,
  TriangleAlert,
  XCircle,
} from "lucide-react";
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../services/notificationService";
import "./Notificationbell.scss";

const TYPE_CONFIG = {
  evaluation: { Icon: Star, color: "#f59e0b" },
  invite: { Icon: Mail, color: "#6366f1" },
  application_approved: { Icon: CheckCircle2, color: "#16a34a" },
  application_rejected: { Icon: XCircle, color: "#ef4444" },
  early_exit: { Icon: TriangleAlert, color: "#f59e0b" },
};

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);

  if (diff < 60) return "Agora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h atrás`;

  return `${Math.floor(diff / 86400)} d atrás`;
}

function isToday(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const ref = useRef(null);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const todayNotifications = useMemo(
    () => notifications.filter((notification) => isToday(notification.createdAt)),
    [notifications]
  );

  const previousNotifications = useMemo(
    () => notifications.filter((notification) => !isToday(notification.createdAt)),
    [notifications]
  );

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await getNotifications();
      setNotifications(data);
    } catch (error) {
      console.error("Erro ao buscar notificações:", error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();

    const interval = setInterval(fetchNotifications, 30_000);

    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOpen = () => {
    setOpen((current) => !current);
  };

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);

      setNotifications((previous) =>
        previous.map((notification) =>
          notification.id === id
            ? { ...notification, isRead: true }
            : notification
        )
      );
    } catch (error) {
      console.error("Erro ao marcar notificação como lida:", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();

      setNotifications((previous) =>
        previous.map((notification) => ({
          ...notification,
          isRead: true,
        }))
      );
    } catch (error) {
      console.error("Erro ao marcar notificações como lidas:", error);
    }
  };

  const renderNotification = (notification) => {
    const config = TYPE_CONFIG[notification.type] ?? {
      Icon: Bell,
      color: "#6b7280",
    };

    const Icon = config.Icon;

    return (
      <button
        key={notification.id}
        type="button"
        className={`nb__item ${
          !notification.isRead ? "nb__item--unread" : ""
        }`}
        onClick={() =>
          !notification.isRead && handleMarkRead(notification.id)
        }
      >
        <div
          className="nb__item-icon"
          style={{
            backgroundColor: `${config.color}18`,
            color: config.color,
          }}
        >
          <Icon size={18} strokeWidth={2.2} />
        </div>

        <div className="nb__item-body">
          <p className="nb__item-title">{notification.title}</p>
          <p className="nb__item-message">{notification.message}</p>
          <span className="nb__item-time">
            {timeAgo(notification.createdAt)}
          </span>
        </div>

        {!notification.isRead && (
          <span className="nb__item-dot" aria-label="Não lida" />
        )}
      </button>
    );
  };

  return (
    <div className="nb" ref={ref}>
      <button
        className="nb__btn"
        type="button"
        onClick={handleOpen}
        aria-label="Notificações"
        aria-expanded={open}
      >
        <Bell size={20} strokeWidth={2} />

        {unreadCount > 0 && (
          <span className="nb__badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="nb__dropdown">
          <div className="nb__header">
            <div>
              <span className="nb__header-title">Notificações</span>

              {unreadCount > 0 && (
                <span className="nb__header-count">
                  {unreadCount} nova{unreadCount !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {unreadCount > 0 && (
              <button
                className="nb__mark-all"
                type="button"
                onClick={handleMarkAllRead}
              >
                Marcar como lidas
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="nb__empty">
              <BellOff size={30} strokeWidth={1.7} />
              <p>Nenhuma notificação ainda.</p>
            </div>
          ) : (
            <div className="nb__list">
              <div className="nb__section">
                <span className="nb__section-title">Hoje</span>

                {todayNotifications.length > 0 ? (
                  todayNotifications.map(renderNotification)
                ) : (
                  <p className="nb__no-today">
                    Nenhuma notificação hoje.
                  </p>
                )}
              </div>

              {previousNotifications.length > 0 && (
                <div className="nb__history">
                  <button
                    type="button"
                    className="nb__history-toggle"
                    onClick={() => setShowHistory((current) => !current)}
                    aria-expanded={showHistory}
                  >
                    <span>
                      Anteriores ({previousNotifications.length})
                    </span>

                    {showHistory ? (
                      <ChevronUp size={17} />
                    ) : (
                      <ChevronDown size={17} />
                    )}
                  </button>

                  {showHistory && (
                    <div className="nb__history-list">
                      {previousNotifications.map(renderNotification)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}